package com.essntl.features.organization.ui.organizationdetails.components

import androidx.compose.foundation.background
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.ColumnScope
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.saveable.rememberSaveable
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.unit.DpSize
import androidx.compose.ui.unit.dp
import com.essntl.core.unsplash.ui.UnsplashImage
import com.essntl.features.file.ui.essntlgallery.EssntlGallery
import com.essntl.features.file.ui.essntlgallery.EssntlGalleryState
import com.essntl.features.organization.domain.model.OrganizationModel
import com.essntl.features.organization.ui.organizationdetails.OrganizationDetailsEvent
import com.essntl.features.organization.ui.organizationdetails.OrganizationDetailsNavigation
import com.essntl.features.organization.ui.organizationdetails.OrganizationDetailsState
import com.essntl.uikit.components.button.tinted.EsMediumTintedButton
import com.essntl.uikit.components.file.FileViewer
import com.essntl.uikit.components.spinner.EsSpinner
import com.essntl.uikit.components.toggle.EsTab
import com.essntl.uikit.components.toggle.TabState
import com.essntl.uikit.components.utils.EsConstants
import com.essntl.uikit.components.utils.EsRowDefaultItem
import com.essntl.uikit.components.utils.spacing
import com.essntl.uikit.theme.esColorScheme
import com.essntl.uikit.theme.esTypography
import com.mohamedrejeb.calf.core.LocalPlatformContext

@Composable
fun ColumnScope.OrganizationDetailsHeader(
    state: OrganizationDetailsState,
    onEvent: (OrganizationDetailsEvent) -> Unit,
    onNavigation: (OrganizationDetailsNavigation) -> Unit,
    tabItems: List<TabState>,
    selectedTabIndex: Int,
    setSelectedTabIndex: (Int) -> Unit,
) {
    val context = LocalPlatformContext.current

    var openEssntlGalleryDialog by rememberSaveable { mutableStateOf(false) }
    var fileUrl by rememberSaveable { mutableStateOf("") }
    var isFileViewerOpened by rememberSaveable { mutableStateOf(false) }

    Box(
        contentAlignment = Alignment.Center,
        modifier = Modifier
            .fillMaxWidth()
            .clip(RoundedCornerShape(EsConstants.SectionBorderRadius))
            .background(MaterialTheme.esColorScheme.bgSecondary)
            .padding(
                vertical = EsConstants.SectionVerticalPadding,
                horizontal = EsConstants.SectionHorizontalPadding,
            ),
    ) {
        if (state.getOrganization.isLoading)
            EsSpinner(
                modifier = Modifier.align(Alignment.CenterStart),
            )
        else {
            Text(
                text = state.organization.value.name,
                color = MaterialTheme.esColorScheme.textPrimary,
                style = MaterialTheme.esTypography.h2Bold32,
                modifier = Modifier.align(Alignment.CenterStart),
            )

            Box(
                contentAlignment = Alignment.Center,
                modifier = Modifier.align(Alignment.CenterEnd),
            ) {
                state.fileState.getAllFile.data?.firstOrNull()?.let { file ->
                    UnsplashImage(
                        url = file.url,
                        selectable = false,
                        imageSize = DpSize(150.dp, 100.dp),
                        onClick = {
                            isFileViewerOpened = true
                            fileUrl = file.url
                        },
                        onDelete = {
                            onEvent(OrganizationDetailsEvent.DeleteLogo)
                        },
                    )
                } ?: EsMediumTintedButton(
                    text = "Add a logo",
                    onClick = { openEssntlGalleryDialog = true },
                )
            }

            EssntlGallery(
                entity = if (state.organization.value.type == OrganizationModel.Type.TravelAgency)
                    EssntlGalleryState.Entity.TA_LOGOS else
                    EssntlGalleryState.Entity.SUPPLIER_LOGOS,
                isOpen = openEssntlGalleryDialog,
                onDismissRequest = { openEssntlGalleryDialog = false },
                multipleSelect = false,
                onAddImages = {
                    val image = it.firstOrNull() ?: return@EssntlGallery
                    onEvent(OrganizationDetailsEvent.AddLogo(image, context))
                },
            )

            FileViewer(
                fileUrl = fileUrl,
                isDialogOpened = isFileViewerOpened,
                onDismissRequest = {
                    isFileViewerOpened = false
                    fileUrl = ""
                },
            )
        }
    }

    Spacer(modifier = Modifier.height(1.spacing))

    Row(
        verticalAlignment = Alignment.CenterVertically,
        horizontalArrangement = Arrangement.SpaceBetween,
        modifier = Modifier
            .fillMaxWidth()
            .clip(RoundedCornerShape(EsConstants.SectionBorderRadius))
            .background(MaterialTheme.esColorScheme.bgSecondary)
            .padding(
                vertical = EsConstants.SectionVerticalPadding,
                horizontal = EsConstants.SectionHorizontalPadding,
            ),
    ) {
        Row(
            verticalAlignment = Alignment.CenterVertically,
            modifier = Modifier
                .fillMaxWidth()
                .clip(RoundedCornerShape(EsConstants.SectionBorderRadius))
                .background(MaterialTheme.esColorScheme.bgSecondary)
                .padding(
                    vertical = EsConstants.SectionVerticalPadding,
                    horizontal = EsConstants.SectionHorizontalPadding,
                ),
        ) {
            EsTab(
                items = tabItems,
                tabWidth = 150.dp,
                selectedItemIndex = selectedTabIndex,
                onClick = { tabState ->
                    if (selectedTabIndex == tabState.index) return@EsTab
                    setSelectedTabIndex(tabState.index)
                    onNavigation(OrganizationDetailsNavigation.Toggle)
                },
            )
        }
    }

    if (selectedTabIndex == 0) {

        Spacer(modifier = Modifier.height(1.spacing))

        Row(
            verticalAlignment = Alignment.CenterVertically,
            modifier =
                Modifier
                    .fillMaxWidth()
                    .clip(RoundedCornerShape(EsConstants.RoundedCornerSize))
                    .padding(
                        vertical = EsConstants.ListRowVerticalPadding,
                        horizontal = EsConstants.ListRowHorizontalPadding + EsConstants.SectionHorizontalPadding,
                    )
                    .height(EsConstants.ListColumnHeaderHeight),
        ) {
            EsRowDefaultItem(
                text = "Name",
                isFirst = true,
                weight = 2f,
            )

            EsRowDefaultItem(
                text = "Email",
            )

            EsRowDefaultItem(
                text = "Phone Number",
            )

            // Date
            EsRowDefaultItem(
                text = "Notes",
                weight = 2f,
            )
        }
    }
}